'use strict';

class LocationsListController {
  constructor($scope,
              $filter,
              $interval,
              $state,
              $stateParams,
              $q,
              utils,
              LocationsService,
              DevicesService,
              OrganizationService,
              TagsService,
              SubscriptionsService,
              VirtualPaginator,
              config,
              PermissionsService,
              ToastsService) {

    const $t = $filter('translate');
    const PREFERRED_LOCATION_TAGS_LIST_PROPERTY = 'locationTags';
    const ORGANIZATIONS_LOCAL_STORAGE_KEY = 'Organizations-All';
    const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
    const SELECTED_LOCATION_LOCAL_STORAGE_KEY = 'Selected-Location';

    // Filter constants
    const CACHE_FILTER = true;
    // Request constants
    const REQUEST_LIMIT = 100;

    $scope.locationPriorities = config.getConfigByKey('locationPriorities').sort((a, b) => b.id - a.id);

    $scope.chooseOrg = utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
    $scope.allOrgs = utils.getStorageItem(ORGANIZATIONS_LOCAL_STORAGE_KEY);
    $scope.thereAreMoreItems = false;
    $scope.timeFormatStr = $t('datetime.mdyHM12');
    $scope.hasFeatureG = utils.hasFeature($scope.chooseOrg, 'g'); // Groups module enabled
    $scope.hasFeatureP = utils.hasFeature($scope.chooseOrg, 'p'); // Location priorities (ranking)
    $scope.loading = true;

    const LOCATIONS_FILTER_LOCAL_STORAGE_KEY = 'Filter-Locations-' + $scope.chooseOrg.id;

    // Batch editing fields
    $scope.selectedLocations = [];
    $scope.isSelectMode = false;
    let selectModeClickCount = 0;

    // Reload locations interval
    const LOCATIONS_UPDATE_INTERVAL = 15000;
    let reloadLocationsInterval;
    $scope.locationsSilentRefresh = false;

    // Put on scope only the stuff we really need there
    angular.extend($scope, {
      canEditLocation: canEditLocation,
      canCreateLocation: canCreateLocation,
      editLocation: editLocation,
      newLocation: newLocation,
      showMoreTags: showMoreTags,
      getLocationAddress: getLocationAddress,
      getLocationOrgName: getLocationOrgName,
      withinChildOrganization: withinChildOrganization,
      filterLocationTags: filterLocationTags,

      // Filter
      searchLocations: searchLocations,
      filterLocations: filterLocations,
      resetFilter: resetFilter,
      toggleFilter: toggleFilter,
      isFilterDirty: isFilterDirty,
      closeFilter: closeFilter,
      toggleSearchWild: toggleSearchWild,
      toggleSearchById: toggleSearchById,
      filterByLocationTag: filterByLocationTag,

      // Batch editing
      toggleLocationSelection: toggleLocationSelection,
      isSelected: isSelected,
      toggleSelectionMode: toggleSelectionMode,
      openBatchEditPanel: openBatchEditPanel,
      closeBatchEditPanel: closeBatchEditPanel,
      toggleBatchEditPanel: toggleBatchEditPanel,
      batchRemoveGroup: batchRemoveGroup,
      batchApply: batchApply
    });

    init();

    // Please, no code execution below this line - only function definitions
    // ---------------------------------------------------------------------

    // Remove locations refresh interval
    $scope.$on('$destroy', () => {
      stopLocationsRefresh();
    });

    function init() {
      initWatchers();

      initFilter()
        .then(() => {
          $scope.paginator = new VirtualPaginator(getOrgLocations);
        });
      initOrgPreferredLocationTags();
    }

    function initWatchers() {
      $scope.$watch('paginator.currentPageItems', () => {
        if (!$scope.paginator || !$scope.paginator.currentPageItems || $scope.paginator.currentPageItems.length === 0) {
          $scope.groupedLocations = [];
          return;
        }

        $scope.groupedLocations = groupLocations($scope.paginator.currentPageItems);

        // Starts refreshing locations
        if ($scope.paginator.currentPage > 1 || $scope.filter.searchVal || $scope.filter.searchWild) {
          if ($scope.locationsSilentRefresh) {
            stopLocationsRefresh();
          }
        } else if (!$scope.locationsSilentRefresh) {
          startLocationsRefresh();
        }
      });
    }

    /**
     * Reload locations list every 15 sec
     * Works only if locations ranking available
     */
    function startLocationsRefresh() {
      if (!$scope.hasFeatureP) {
        return;
      }
      if (reloadLocationsInterval) {
        $interval.cancel(reloadLocationsInterval);
      }

      $scope.locationsSilentRefresh = true;
      reloadLocationsInterval = $interval(() => {
        $scope.paginator.reload();
      }, LOCATIONS_UPDATE_INTERVAL);
    }

    function stopLocationsRefresh() {
      $scope.locationsSilentRefresh = false;
      if (reloadLocationsInterval) {
        $interval.cancel(reloadLocationsInterval);
      }
    }

    /**
     * Group locations by priority category and rank
     * @param {Array<{}>} data List of locations
     */
    function groupLocations(data) {
      if (!data || data.length === 0) {
        return;
      }

      if (!$scope.hasFeatureP) {
        data.forEach(e => {
          e.colorStyle = getLocationColorMark(e);
        });
        return [
          {
            locations: data
          }
        ];
      }

      return $scope.locationPriorities.map(e => ({
        ...e,
        name: $t(e.name),
        desc: $t(e.desc),
        colorStyle: e.color + '-mark',
        locations: data
          .filter(location => location.priorityCategory === e.id || (!location.priorityCategory && e.id === 0))
          .sort((a, b) => (b.priorityRank || 0) - (a.priorityRank || 0))
      }))
        .sort((a, b) => b.id - a.id);
    }

    function getLocationColorMark(location) {
      let result = 'red-mark';
      if (location.organizationId) {
        if (location.organizationId == $scope.chooseOrg.id) {
          result = 'green-mark';
        } else {
          result = withinChildOrganization(location) ? 'orange-mark' : 'grey-mark';
        }
      }
      return result;
    }

    // #region Filter Methods

    function initFilter() {
      $scope.filter = {};
      $scope.filter.searchVal = $stateParams.searchBy || '';

      // Restore from cache only if nothing was transferred via URL query parameters
      if (!isFilterDirty()) {
        initFilterFromCache();
      }

      saveFilter();

      // #region Watchers

      /* $scope.$watch('filter', function() {
        // We save filter only after it was applied
        saveFilter();
      }, true); */

      $scope.$watchGroup([
        'filter.countryId',
        'selectedCountry'
      ], () => changeCountry());

      // #region Only allow to select one field in Batch edit form

      if ($scope.hasFeatureG) {
        $scope.$watch('selectedGroupTagForBatchEdit', (newValue, oldValue) => {
          if (newValue != null && newValue !== oldValue) {
            $scope.selectedOrgIdForBatchEdit = undefined;
            $scope.selectedTagsForBatchEdit = [];
            $scope.selectedSubscriptionIdForBatchEdit = undefined;
          }
        });
      }

      $scope.$watch('selectedOrgIdForBatchEdit', (newValue, oldValue) => {
        if (newValue != null && newValue !== oldValue) {
          $scope.selectedGroupTagForBatchEdit = null;
          $scope.selectedTagsForBatchEdit = [];
          $scope.selectedSubscriptionIdForBatchEdit = undefined;
        }
      });

      $scope.$watch('selectedTagsForBatchEdit', (newValue, oldValue) => {
        if (newValue && newValue.length > 0) {
          $scope.selectedGroupTagForBatchEdit = null;
          $scope.selectedOrgIdForBatchEdit = undefined;
          $scope.selectedSubscriptionIdForBatchEdit = undefined;
        }
      }, true);

      $scope.$watch('selectedSubscriptionIdForBatchEdit', (newValue, oldValue) => {
        if (newValue != null && newValue !== oldValue) {
          $scope.selectedGroupTagForBatchEdit = null;
          $scope.selectedOrgIdForBatchEdit = undefined;
          $scope.selectedTagsForBatchEdit = [];
        }
      });

      $scope.$watchGroup(['selectedLocations', 'selectedOrgIdForBatchEdit'], () => {

        if (!$scope.selectedLocations || $scope.selectedLocations.length === 0) {
          $scope.canBatchEditTags = false;
          $scope.canBatchEditOrgs = false;
          $scope.canBatchEditGroups = false;
          $scope.batchEditTagsMsg = '';
          $scope.batchEditOrgsMsg = '';
          $scope.batchEditGroupsMsg = '';
          return;
        }

        /**
         * If we select some location with no organization
         * @type {boolean}
         */
        let locationsSelectedWithNoOrganization = $scope.selectedLocations.some(l => !l.organizationId);

        /**
         * If we select some locations from multiple organizations
         * @type {boolean|boolean}
         */
        let locationsSelectedFromMultipleOrganizations = $scope.selectedLocations.some(l => l.organizationId !== $scope.selectedLocations[0].organizationId);

        $scope.showBatchEditTags = true;
        $scope.canBatchEditTags = !locationsSelectedWithNoOrganization;
        $scope.batchEditTagsMsg = (!$scope.canBatchEditTags && locationsSelectedWithNoOrganization)
          ? $t('locations.list.batch.msg.tagsNoOrg')
          : '';

        $scope.showBatchEditOrgs = true;
        $scope.canBatchEditOrgs = $scope.allOrgs && $scope.allOrgs.length > 0;
        if ($scope.canBatchEditOrgs && $scope.selectedOrgIdForBatchEdit && $scope.selectedLocations.some(l => l.organizationId === $scope.selectedOrgIdForBatchEdit)) {
          $scope.batchEditOrgsMsg = $t('locations.list.batch.msg.locationsInTargetOrganization');
        } else {
          $scope.batchEditOrgsMsg = '';
        }

        $scope.showBatchEditGroups = $scope.hasFeatureG;
        $scope.canBatchEditGroups = !locationsSelectedFromMultipleOrganizations
          && !locationsSelectedWithNoOrganization;

        // Reset groups (grouping tags) selection when organization for batch edit is selected
        if ($scope.canBatchEditOrgs && $scope.selectedOrgIdForBatchEdit) {
          $scope.selectedGroupTagForBatchEdit = null;
        }

        // Calculating available groups for batch editing
        // And check feature "g" for organization
        if ($scope.canBatchEditGroups) {
          let orgId = $scope.selectedLocations[0].organizationId;
          let org = $scope.allOrgs.find(o => o.id === orgId);
          if (!orgId || !org || !utils.hasFeature(org, 'g')) {
            $scope.batchEditGroups = [];
          } else {
            $scope.batchEditGroups = $scope.allGroups || [];
          }
          $scope.canBatchEditGroups = $scope.canBatchEditGroups && $scope.batchEditGroups.length > 0;
          if ($scope.selectedGroupTagForBatchEdit && !$scope.batchEditGroups.some(g => g.tag === $scope.selectedGroupTagForBatchEdit)) {
            $scope.selectedGroupTagForBatchEdit = null;
          }
        } else {
          $scope.batchEditGroups = [];
          $scope.selectedGroupTagForBatchEdit = null;
        }

        $scope.batchEditGroupsMsg = '';
        if (!$scope.canBatchEditGroups) {
          if (locationsSelectedFromMultipleOrganizations) {
            $scope.batchEditGroupsMsg = $t('locations.list.batch.msg.groupsMultipleOrgs');
          } else if (locationsSelectedWithNoOrganization) {
            $scope.batchEditGroupsMsg = $t('locations.list.batch.msg.groupsNoOrg');
          } else if ($scope.batchEditGroups.length === 0) {
            $scope.batchEditGroupsMsg = $t('locations.list.batch.msg.groupsNoGroups');
          }
        }
      }, true);

      // #endregion

      if ($scope.hasFeatureG) {
        $scope.$watchGroup(['filter.orgId', 'allGroups'], () => {
          if (!$scope.allGroups) {
            $scope.locationGroups = void 0;
            return;
          }

          $scope.locationGroups = $scope.allGroups;
          if ($scope.filter.orgId) {
            if ($scope.filter.groupTag && !$scope.locationGroups.some(g => g.tag === $scope.filter.groupTag)) {
              $scope.filter.groupTag = void 0;
              $scope.selectedLocationGroup = void 0;
            }
          }
        });
      }

      // #endregion

      // Get child organizations if they are exist
      $scope.childOrgs = !$scope.chooseOrg.parentId
        ? utils.getChildOrganizations($scope.allOrgs, $scope.chooseOrg.id)
        : [];

      return $q.all([
        getLocationTags(),
        getDeviceTypes(),
        getLocationTypes(),
        getCountries(),
        getLocationGroups(),
        initSubscriptions()
      ]);
    }

    function initOrgPreferredLocationTags() {
      return OrganizationService.getObjectOrProperty($scope.chooseOrg.id, PREFERRED_LOCATION_TAGS_LIST_PROPERTY)
        .then(property => {
          $scope.orgPreferredLocationTags = [];
          if (property && property.value != null) {
            $scope.orgPreferredLocationTags = property.value.split(',').filter(badge => badge && badge.length !== 0);
          }
        }, err => {
          utils.handleApiError(err);
        });
    }

    function initSubscriptions() {
      let brand = utils.getBrandName($scope.chooseOrg.id, $scope.allOrgs);
      return SubscriptionsService.getOrganizationSubscriptions($scope.chooseOrg.id, brand)
        .then((subscriptions) => {
          $scope.orgSubscriptions = subscriptions;
        });
    }

    /**
     * Init filter object from localStorage cache
     */
    function initFilterFromCache() {
      if (CACHE_FILTER) {
        let params = utils.getStorageItem(LOCATIONS_FILTER_LOCAL_STORAGE_KEY);
        if (params) {
          $scope.filter = params;
        }
      }
    }

    /**
     * Save filter to localStorage
     */
    function saveFilter() {
      stopLocationsRefresh();

      if (CACHE_FILTER) {
        utils.setStorageItem(LOCATIONS_FILTER_LOCAL_STORAGE_KEY, $scope.filter);
      }
    }

    /**
     * Is filter has some values set
     * @param {boolean} filterOnly To check only filter without search value
     * @return {boolean}
     */
    function isFilterDirty(filterOnly) {
      if (!$scope.filter || $scope.filter.searchById) {
        return false;
      }

      let tempFilter = angular.copy($scope.filter);
      if (filterOnly) {
        tempFilter.searchVal = null;
        tempFilter.searchWild = void 0;
        tempFilter.searchById = void 0;
      }
      return Object.keys(tempFilter)
        .some(key => (key === 'locationTags' || key === 'deviceTypeIds' || key === 'locationPrioritiesIds')
          ? tempFilter[key] && tempFilter[key].length > 0
          : !!tempFilter[key]);
    }

    /**
     * Toggle filter UI visibility
     */
    function toggleFilter() {
      $('#filter').transition('fade down');
    }

    /* Toggle on/off organization-free search */
    function toggleSearchWild() {
      $scope.filter.searchWild = !$scope.filter.searchWild;
      $scope.filter.searchById = false;
      resetFilter(true);
    }

    /* Toggle on/off search by user ID */
    function toggleSearchById() {
      $scope.filter.searchById = !$scope.filter.searchById;
      $scope.filter.searchWild = false;
      toggleSelectionMode(true);
      resetFilter();
    }

    /**
     * Hide filter UI
     */
    function closeFilter() {
      let visible = $('#filter').transition('is visible');
      if (visible) {
        $('#filter').transition('hide');
      }
    }

    function resetFilter(isSearchWildTriggered) {
      if ($scope.loading || !$scope.paginator) {
        return;
      }

      $scope.filter = !isSearchWildTriggered ? {
        searchVal: $scope.filter.searchVal,
        searchById: !!$scope.filter.searchById,
      } : {
        groupTag: void 0,
        orgId: void 0,
        ...$scope.filter
      };

      // if (isSearchWildTriggered) {
      //   delete $scope.filter.groupTag;
      //   delete $scope.filter.orgId;
      //   $scope.selectedLocationGroup = void 0;
      //   $scope.selectedChildOrg = void 0;
      // } else {
      //   $scope.filter = {};
      //   $scope.selectedLocationTags = void 0;
      //   $scope.selectedCountry = void 0;
      //   $scope.selectedState = void 0;
      //   $scope.selectedDeviceTypes = void 0;
      //   $scope.selectedSubscription = void 0;
      //   $scope.selectedLocationGroup = void 0;
      //   $scope.selectedLocationType = void 0;
      //   $scope.selectedChildOrg = void 0;
      // }

      closeFilter();
      saveFilter();
      $scope.paginator.reload();
    }

    function filterLocationTags(tag, index, array) {
      if (!$scope.orgPreferredLocationTags || $scope.orgPreferredLocationTags.length === 0) {
        return false;
      }
      return $scope.orgPreferredLocationTags.includes(tag.tag);
    }

    function searchLocations() {
      if ($scope.loading || !$scope.paginator) {
        return;
      }
      saveFilter();
      $scope.paginator.reload();
    }

    function filterLocations() {
      if ($scope.loading || !$scope.paginator) {
        return;
      }
      closeFilter();
      saveFilter();
      $scope.paginator.reload();
    }

    /**
     * Get location tags for current organization
     * @return {Promise}
     */
    function getLocationTags() {
      let tagsType = 2; // Location tags
      let tagsCategory = 0; // Default category

      return TagsService.getPopularTags($scope.chooseOrg.id, tagsType, tagsCategory, 100)
        .then(data => {
            $scope.locationTags = data.tags || [];
          },
          err => {
            $scope.loading = false;
            utils.handleApiError(err);
          });
    }

    /**
     * Get device types allowed in current organization
     * @returns {Promise}
     */
    function getDeviceTypes() {
      return DevicesService.getDeviceTypes()
        .then(data => {
          if (data && data.deviceTypes) {
            // Init device type filter
            $scope.allowedDeviceTypes = utils.getOrgAllowedDeviceTypes(data.deviceTypes, null, $scope.chooseOrg);
          }
        }, err => {
          $scope.loading = false;
          utils.handleApiError(err);
        });
    }

    /**
     * Get location types
     * @return {{name: *, id: *}[]}
     */
    function getLocationTypes() {
      $scope.locationsTypes = (config.getLocationTypes() || [])
        .map(lt => ({
          id: lt.id,
          name: $t(lt.name)
        }));
      return $scope.locationsTypes;
    }

    /**
     * Get countries and states
     * @return {Promise}
     */
    function getCountries() {
      return LocationsService.getCountries()
        .then(data => {
            $scope.countries = data.countries;
          },
          err => {
            $scope.loading = false;
            utils.handleApiError(err);
          });
    }

    /**
     * Change country and show list of states respectfully
     */
    function changeCountry() {
      if ((!$scope.selectedCountry && $scope.filter.countryId) || ($scope.selectedCountry && !$scope.filter.countryId)) {
        return;
      }
      if (!$scope.selectedCountry) {
        $scope.states = void 0;
        $scope.filter.stateId = void 0;
        return;
      }
      $scope.states = $scope.selectedCountry.states;
      if (!$scope.states || $scope.states.length <= 0) {
        $scope.filter.stateId = void 0;
      } else if ($scope.filter.stateId && !$scope.states.some(s => $scope.filter.stateId === s.id)) {
        $scope.filter.stateId = void 0;
      }
    }

    /**
     * Get location groups for current organization and all child organizations
     */
    function getLocationGroups() {
      if (!$scope.hasFeatureG) {
        return $q.resolve();
      }

      let tagType = 2; // Location tags
      let tagCategory = 2; // Groups category

      return TagsService.getPopularTags($scope.chooseOrg.id, tagType, tagCategory, 50)
        .then(data => {
          if (data && data.tags) {
            $scope.allGroups = data.tags;
          } else {
            $scope.allGroups = void 0;
            return;
          }

          return $scope.allGroups;
        });
    }

    // endregion

    // #region Actions for Locations

    function canEditLocation(location) {
      if (!location) {
        return false;
      }
      return PermissionsService.canIEditLocation(location);
    }

    function canCreateLocation() {
      return PermissionsService.canICreateLocation();
    }

    /**
     * Goto Edit location UI
     * @param location location to edit
     * @param {string} tabName tab name to open automatically
     */
    function editLocation(location, $event, tabName) {
      if ($scope.loading || !canEditLocation(location)) {
        return;
      }
      if ($scope.isSelectMode) {
        $scope.toggleLocationSelection(location, $event);
        return;
      }
      // If Location is not at any Organization
      if (!location.organizationId) {
        return;
      }
      utils.setStorageItem(SELECTED_LOCATION_LOCAL_STORAGE_KEY, location);
      $state.go('main.locations.edit', { locationId: location.id, tab: tabName });
    }

    function newLocation() {
      if (!canCreateLocation()) {
        return;
      }
      if ($scope.loading) {
        return;
      }
      utils.removeStorageItem('selected-location-id');
      $state.go('main.locations.new');
    }

    // #endregion

    /**
     * Get locations list by parameters
     * @return {Promise}
     */
    function getOrgLocations() {
      let params = {
        tree: false,
        getTags: true,
        sortBy: $scope.hasFeatureP ? 'priorityCategory' : 'creationDateMs',
        sortOrder: 'desc'
      };

      if ($scope.filter.searchVal) {
        if ($scope.filter.searchById) {
          params.locationId = $scope.filter.searchVal;
        } else {
          params.searchBy = '*' + $scope.filter.searchVal + '*';
        }
      }
      if ($scope.filter.locationTags || $scope.filter.groupTag) {
        let searchTags = $scope.filter.locationTags || [];
        if ($scope.filter.groupTag) {
          searchTags = searchTags.concat($scope.filter.groupTag);
        }
        params.searchTag = searchTags;
      }
      if ($scope.filter.deviceTypeIds) {
        params.deviceType = $scope.filter.deviceTypeIds;
      }
      if ($scope.filter.locationTypeId) {
        params.locationType = $scope.filter.locationTypeId;
      }
      if ($scope.filter.subscriptionId) {
        params.servicePlanId = $scope.filter.subscriptionId;
      }
      if ($scope.filter.countryId) {
        params.countryId = $scope.filter.countryId;
      }
      if ($scope.filter.stateId) {
        params.stateId = $scope.filter.stateId;
      }

      // Exclude "Empty" priority by default
      if ($scope.hasFeatureP && $scope.filter.locationPrioritiesIds && $scope.filter.locationPrioritiesIds.length > 0) {
          params.priorityCategory = $scope.filter.locationPrioritiesIds;
      }

      // Always limit requests
      params.limit = REQUEST_LIMIT;

      // $scope.selectedLocationGroup may be undefined even if $scope.filter.groupId is defined - it's dropdown bug
      let selectedGroup = ($scope.filter.groupTag && $scope.allGroups)
        ? $scope.allGroups.find(g => g.tag === $scope.filter.groupTag)
        : void 0;
      let orgId = (selectedGroup ? selectedGroup.organizationId : void 0) // Org of selected group
        || ($scope.filter ? $scope.filter.orgId : void 0) // or org, selected in filter
        || $scope.chooseOrg.id; // or current org

      // Search across all organizations
      if ($scope.filter.searchWild || $scope.filter.searchById) {
        // Do not filter by priority
        if ($scope.hasFeatureP) {
          $scope.filter.locationPrioritiesIds = [];
          params.priorityCategory = void 0;
        }
        orgId = void 0;
      }

      $scope.loading = $scope.locationsSilentRefresh ? false : true;
      return LocationsService.getLocations(orgId, params)
        .then(data => {
          $scope.loading = false;
          if (!data.locations) {
            $scope.thereAreMoreItems = false;
            $scope.locations = [];
            $scope.selectedLocations = [];
            return $scope.locations;
          }

          // Limiting request message
          $scope.thereAreMoreItems = data.collectionTotalSize && data.collectionTotalSize === REQUEST_LIMIT;

          $scope.locations = data.locations;
          $scope.selectedLocations = $scope.selectedLocations.filter(sl => $scope.locations.some(l => l.id === sl.id));
          return $scope.locations;

        }, (error) => {
          $scope.loading = false;
          utils.handleApiError(error);
          return $q.reject(error);
        });
    }

    /**
     * Goto Edit Location UI on tags tab
     * @param $event
     * @param location
     */
    function showMoreTags($event, location) {
      $event.stopPropagation();
      $scope.editLocation(location, null, 'dashboard');
    }

    /**
     * Filter locations list by particular location tag
     * @param $event
     * @param tag
     */
    function filterByLocationTag($event, tag) {
      $event.stopPropagation();
      if (tag && tag.tag) {
        $scope.filter.locationTags = tag.tag;
        $scope.filterLocations();
      }
    }

    function getLocationAddress(location) {
      let address = utils.generateAddress(location);
      if (!address) {
        return $t('common.unknown-address');
      }
      return address;
    }

    function getLocationOrgName(location) {
      if (!location || !location.organizationId) {
        return $t('locations.list.notAtOrg');
      }
      let org = $scope.allOrgs.find(element => {
        return location.organizationId === element.id;
      });
      if (org && org.name) {
        return $t('locations.list.atOrg', { name: org.name });
      } else {
        return $t('common.unknownOrganization');
      }
    }

    /* Test location if it's in related organization */
    function withinChildOrganization(location) {
      let result = false;
      if ($scope.childOrgs && $scope.childOrgs.length) {
        result = $scope.childOrgs.some(org => org.id === location.organizationId);
      }
      return result;
    }

    // #region Batch edit

    /**
     * Toggle location selection
     */
    function toggleLocationSelection(location, event) {
      if (event) {
        event.stopPropagation();
      }
      if (!canEditLocation(location)) {
        return;
      }
      let currentLocations = angular.copy($scope.selectedLocations);
      if (isSelected(location)) {
        $scope.selectedLocations = currentLocations.filter(l => l.id !== location.id);
      } else {
        currentLocations.push(location);
        $scope.selectedLocations = currentLocations;
      }
    }

    /**
     * Is location selected
     * @param {*} location
     * @return {boolean}
     */
    function isSelected(location) {
      return $scope.selectedLocations.some((l) => l.id === location.id);
    }

    function toggleSelectionMode(resetToDefault) {
      stopLocationsRefresh();
      if (!$scope.isSelectMode) {
        $scope.isSelectMode = true; // Just turn it on
      }

      selectModeClickCount++;

      // To allow to select all via second click, we need to 'switch off' selection mode only on the third click
      if (selectModeClickCount === 2) {
        $scope.selectedLocations = [].concat($scope.paginator.currentPageItems);
      }
      if (resetToDefault || selectModeClickCount === 3) {
        $scope.isSelectMode = false;
        selectModeClickCount = 0;
        if ($('#batchEdit').hasClass('visible')) {
          closeBatchEditPanel();
        }
      }

      if (!$scope.isSelectMode) {
        // After we switched off the selection mode
        $scope.selectedLocations = [];
      }
    }

    function openBatchEditPanel() {
      let opened = $('#batchEdit').transition('is visible');
      if (!opened) {
        $('#batchEdit').transition('fade down');
        $('html, body').animate({
          scrollTop: 0
        }, 500);
      }
    }

    function closeBatchEditPanel() {
      $scope.loading = false;
      let opened = $('#batchEdit').transition('is visible');
      if (opened) {
        $('#batchEdit').transition('hide');
      }
      if ($scope.isSelectMode) {
        $scope.selectedLocations = [];
        $scope.isSelectMode = false;
        selectModeClickCount = 0;
      }
      $scope.selectedGroupTagForBatchEdit = null;
      $scope.selectedOrgIdForBatchEdit = undefined;
      $scope.selectedTagsForBatchEdit = [];
      $scope.selectedSubscriptionIdForBatchEdit = undefined;
    }

    function toggleBatchEditPanel() {
      $('#batchEdit').transition('fade down');
    }

    function batchRemoveGroup() {
      if ($scope.loading || !$scope.hasFeatureG || !$scope.selectedLocations || $scope.selectedLocations.length <= 0 || !$scope.selectedGroupTagForBatchEdit) {
        return;
      }

      $scope.loading = true;
      let tagType = 2; // Location tag

      // TODO: Optimize API to delete multiple tags from multiple locations in one request
      let promises = $scope.selectedLocations.map((location) => {
        return TagsService.deleteTags($scope.chooseOrg.id, tagType, location.id, $scope.selectedGroupTagForBatchEdit);
      });

      $q.all(promises)
        .then(() => {
          closeBatchEditPanel();
          ToastsService.success(null, $t('locations.list.batch.group-remove-success'), 'success', 2);
          $scope.paginator.reload();
        }, (err) => {
          closeBatchEditPanel();
          $scope.loading = false;
          utils.handleApiError(err);
          $scope.paginator.reload();
        });
    }

    function batchApply() {
      if (!$scope.selectedLocations || $scope.selectedLocations.length <= 0) {
        return;
      }
      let locations = $scope.selectedLocations.map(l => l.id);
      $scope.loading = true;

      // Move to selected organization
      if ($scope.selectedOrgIdForBatchEdit && $scope.canBatchEditOrgs) {
        return LocationsService.manageMultipleLocations($scope.selectedOrgIdForBatchEdit, locations)
          .then(() => {
            closeBatchEditPanel();
            ToastsService.success(null, $t('locations.list.batch.org-change-success'), 'success', 2);
            $scope.paginator.reload();
          }, err => {
            closeBatchEditPanel();
            $scope.loading = false;
            utils.handleApiError(err);
            $scope.paginator.reload();
          });

      // Assign selected tags (including grouping tag)
      } else if (($scope.selectedGroupTagForBatchEdit && $scope.canBatchEditGroups) || ($scope.selectedTagsForBatchEdit && $scope.selectedTagsForBatchEdit.length > 0 && $scope.canBatchEditTags)) {
        let groupedLocations = {}; // Grouped by organizationId
        $scope.selectedLocations.forEach(l => {
          if (!l.organizationId) {
            return;
          }
          if (!groupedLocations[l.organizationId]) {
            groupedLocations[l.organizationId] = [];
          }
          groupedLocations[l.organizationId].push(l);
        });

        let promises = [];
        for (const [organizationId, locations] of Object.entries(groupedLocations)) {
          let tagsToApply = [];

          // Add common tags
          if ($scope.selectedTagsForBatchEdit && $scope.selectedTagsForBatchEdit.length > 0) {
            $scope.selectedTagsForBatchEdit.forEach(tag => {
              locations.forEach((location) => {
                tagsToApply.push({
                  id: location.id,
                  type: 2, // Location tag
                  tag: tag
                });
              });
            });
          }

          // Add grouping tag
          if ($scope.selectedGroupTagForBatchEdit) {
            locations.forEach((location) => {
              tagsToApply.push({
                id: location.id,
                type: 2, // Location tag
                tag: $scope.selectedGroupTagForBatchEdit,
                category: 2 // Group category
              });
            });
          }

          promises.push(TagsService.saveTags(organizationId, tagsToApply));
        }

        return $q.all(promises)
          .then(() => {
            closeBatchEditPanel();
            ToastsService.success(null, $t('locations.list.batch.tags-add-success'), 'success', 2);
            $scope.paginator.reload();
          }, err => {
            closeBatchEditPanel();
            $scope.loading = false;
            utils.handleApiError(err);
            $scope.paginator.reload();
          });

      // Assign subscription (service plan) to locations
      } else if ($scope.selectedSubscriptionIdForBatchEdit && $scope.orgSubscriptions && $scope.orgSubscriptions.length > 0) {
        let locations = $scope.selectedLocations.map(l => {
          return {
            locationId: l.id
          };
        });
        return SubscriptionsService.grantSubscriptionToLocations($scope.selectedSubscriptionIdForBatchEdit, locations, $scope.selectedOrgIdForBatchEdit)
          .then(() => {
            closeBatchEditPanel();
            ToastsService.success(null, $t('locations.list.batch.subscription-assign-success'), 'success', 2);
            $scope.paginator.reload();
          }, err => {
            closeBatchEditPanel();
            $scope.loading = false;
            utils.handleApiError(err);
            $scope.paginator.reload();
          });
      } else {
        $scope.loading = false;
      }
    }

    // #endregion
  }
}

export default angular
  .module('main.locationsList.controller', [])
  .controller('LocationsListController', LocationsListController)
  .name;
